"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { subscribePageVisibility } from "@/lib/page-visibility";
import { TinyXIcon } from "@/components/icons/TinyXIcon";
import { UserSilhouetteFilledIcon } from "@/components/icons/UserSilhouetteFilledIcon";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";
const RETRY_INTERVAL = 15;

interface ServerUnavailableScreenProps {
  onServerBack?: () => void;
  /** Copy overrides for non-outage variants (e.g. a dead session that needs a
   *  fresh sign-in). Defaults keep the original server-outage copy. */
  heading?: string;
  subtext?: string;
  /** When false, the countdown loop never fires onServerBack on its own (the
   *  manual Retry button keeps working). For variants where a passing /health
   *  probe does NOT mean recovery — e.g. a dead session: the server is up but
   *  auto-reloading would just cycle back to the same screen. */
  autoRetry?: boolean;
}

export function ServerUnavailableScreen({
  onServerBack,
  heading,
  subtext,
  autoRetry = true,
}: ServerUnavailableScreenProps) {
  const { signOut } = useAuth();
  const [seconds, setSeconds] = useState(RETRY_INTERVAL);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);

    try {
      const res = await fetch(`${SERVER_URL}/health`, {
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) {
        onServerBack?.();
        return;
      }
    } catch {
      // Server still down
    }

    setRetrying(false);
    setSeconds(RETRY_INTERVAL);
  }, [retrying, onServerBack]);

  useEffect(() => {
    if (!autoRetry || retrying) return;
    if (seconds <= 0) {
      handleRetry();
      return;
    }
    const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [autoRetry, seconds, retrying, handleRetry]);

  /*
   * THE COUNTDOWN IS A FOREGROUND FALLBACK, NOT THE RECOVERY RAIL.
   *
   * A wall-clock timer is not a recovery rail in a tab the browser is allowed
   * to freeze. Chrome throttles `setTimeout` to roughly once a minute in a
   * hidden tab and stops it outright under Memory Saver, so fifteen one-second
   * ticks can take fifteen real minutes — or never finish. Someone who tabs
   * away and comes back meets a countdown that barely moved, on a screen that
   * is blocking the whole app, with their network long since fine.
   *
   * Founder, 09-12: "showing the screen itself is fine but showing it even
   * after getting back the network is not." So the enders are the events that
   * actually mean something changed — the tab being looked at again, and the
   * browser regaining the network — and neither of them is a clock. The
   * countdown stays for the foreground case, where it is honest.
   *
   * `subscribePageVisibility` pairs `focus` with `visibilitychange`, which
   * matters here: webviews skip `visibilitychange` on restore. Probing on a
   * plain focus click is deliberate — this screen is blocking, so any sign of
   * attention is worth one cheap /health call, and `retrying` guards overlap.
   */
  useEffect(() => {
    if (!autoRetry) return;
    const probe = () => void handleRetry();
    const unsubscribe = subscribePageVisibility((visible) => {
      if (visible) probe();
    });
    window.addEventListener("online", probe);
    return () => {
      unsubscribe();
      window.removeEventListener("online", probe);
    };
  }, [autoRetry, handleRetry]);

  const circumference = 2 * Math.PI * 12;
  const ringOffset = circumference * (1 - seconds / RETRY_INTERVAL);

  return (
    <>
      <style>{`
        .suv {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: var(--bg-base);
          color: var(--fg-default);
          font-family: var(--font-inter, 'Inter', system-ui, -apple-system, sans-serif);
          -webkit-font-smoothing: antialiased;
          overflow: hidden;
        }

        .suv__content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 420px;
          width: 100%;
          padding: 0 24px;
        }

        /* ── Connection visualization ── */
        .suv__viz {
          position: relative;
          width: 280px;
          height: 100px;
          margin-bottom: 36px;
          opacity: 0;
          animation: suv-fade-up 0.6s ease forwards 0.1s;
        }

        .suv__node {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          box-shadow: var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.1));
        }

        .suv__node--you {
          width: 48px;
          height: 48px;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }

        .suv__node--logo {
          width: 56px;
          height: 56px;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        }

        .suv__node--server {
          width: 48px;
          height: 48px;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
        }

        .suv__avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--bg-muted);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .suv__avatar svg {
          width: 14px;
          height: 14px;
          color: var(--fg-subtle);
        }

        .suv__logo-icon {
          width: 32px;
          height: 32px;
        }

        .suv__server-bars {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
        }

        .suv__server-bar {
          width: 18px;
          height: 3px;
          border-radius: 1.5px;
          background: var(--fg-faint);
        }

        .suv__server-bar:nth-child(2) {
          width: 14px;
        }

        /* Connection lines */
        .suv__line {
          position: absolute;
          top: 50%;
          height: 2px;
          background: var(--border-default);
          transform: translateY(-50%);
        }

        .suv__line--left {
          left: 54px;
          width: 58px;
        }

        .suv__line--right {
          right: 54px;
          width: 58px;
        }

        /* Animated signal dots */
        .suv__dot {
          position: absolute;
          top: 50%;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--color-orange, #ff8a3d);
          transform: translateY(-50%);
          opacity: 0;
        }

        .suv__dot--ok {
          left: 54px;
          animation: suv-travel-right 2s ease-in-out infinite;
        }

        .suv__dot--fail {
          right: 54px;
          animation: suv-travel-fail 2.4s ease-in-out infinite;
        }

        @keyframes suv-travel-right {
          0% { left: 54px; opacity: 0; }
          10% { opacity: 1; }
          45% { left: 108px; opacity: 1; }
          55% { opacity: 0; }
          100% { opacity: 0; }
        }

        @keyframes suv-travel-fail {
          0% { right: 108px; opacity: 0; }
          10% { opacity: 1; }
          30% { right: 80px; opacity: 1; }
          40% { right: 85px; opacity: 0.6; }
          50% { right: 75px; opacity: 0.3; }
          55% { right: 80px; opacity: 0; }
          100% { opacity: 0; }
        }

        /* X mark on broken connection */
        .suv__x {
          position: absolute;
          right: 72px;
          top: 50%;
          transform: translate(50%, -50%);
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--bg-base);
          border: 1.5px solid var(--color-orange, #ff8a3d);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: suv-x-pulse 2.4s ease-in-out infinite;
        }

        .suv__x svg {
          width: 7px;
          height: 7px;
          color: var(--color-orange, #ff8a3d);
        }

        @keyframes suv-x-pulse {
          0%, 100% { transform: translate(50%, -50%) scale(1); }
          50% { transform: translate(50%, -50%) scale(1.1); }
        }

        /* Node labels */
        .suv__labels {
          position: absolute;
          bottom: -4px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: space-between;
          padding: 0 2px;
        }

        .suv__label {
          font-size: 10px;
          font-weight: 500;
          color: var(--fg-faint);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          text-align: center;
        }

        .suv__label:first-child { width: 48px; }
        .suv__label--center {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
        .suv__label:last-child { width: 48px; }

        /* ── Status pill ── */
        .suv__pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          border-radius: 100px;
          background: color-mix(in srgb, var(--color-orange, #ff8a3d) 8%, transparent);
          border: 1px solid color-mix(in srgb, var(--color-orange, #ff8a3d) 15%, transparent);
          margin-bottom: 20px;
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.3s;
        }

        .suv__pill-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--color-orange, #ff8a3d);
          animation: suv-pulse 2s ease-in-out infinite;
        }

        @keyframes suv-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .suv__pill-text {
          font-size: 12px;
          font-weight: 500;
          color: var(--color-orange, #ff8a3d);
          letter-spacing: 0.01em;
        }

        /* ── Typography ── */
        .suv__heading {
          font-size: 22px;
          font-weight: 600;
          color: var(--fg-default);
          text-align: center;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.4s;
        }

        .suv__subtext {
          font-size: 14px;
          line-height: 1.6;
          color: var(--fg-muted);
          text-align: center;
          margin-bottom: 32px;
          max-width: 320px;
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.5s;
        }

        @keyframes suv-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── Action card ── */
        .suv__card {
          width: 100%;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          border-radius: 16px;
          padding: 24px;
          box-shadow: var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.1));
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.6s;
        }

        .suv__retry-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 24px;
          font-family: var(--font-inter, 'Inter', system-ui, sans-serif);
          font-size: 14px;
          font-weight: 500;
          color: var(--bg-base);
          background: var(--fg-default);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .suv__retry-btn:hover { opacity: 0.9; }
        .suv__retry-btn:active { opacity: 0.85; }
        .suv__retry-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .suv__spinner {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 2px solid transparent;
          border-top-color: var(--bg-base);
          border-right-color: var(--bg-base);
          animation: suv-spin 0.6s linear infinite;
        }

        @keyframes suv-spin { to { transform: rotate(360deg); } }

        .suv__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-subtle, var(--border-default));
        }

        .suv__countdown {
          font-size: 13px;
          color: var(--fg-subtle);
        }

        .suv__countdown span {
          color: var(--fg-muted);
          font-weight: 600;
          font-variant-numeric: tabular-nums;
        }

        .suv__ring {
          width: 28px;
          height: 28px;
          transform: rotate(-90deg);
        }

        .suv__ring-bg {
          fill: none;
          stroke: var(--border-default);
          stroke-width: 2;
        }

        .suv__ring-fill {
          fill: none;
          stroke: var(--color-orange, #ff8a3d);
          stroke-width: 2;
          stroke-linecap: round;
          transition: stroke-dashoffset 1s linear;
        }

        /* ── Fine print ── */
        .suv__fine-print {
          margin-top: 20px;
          text-align: center;
          font-size: 12px;
          color: var(--fg-faint);
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.7s;
        }

        .suv__fine-print a {
          color: var(--fg-subtle);
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: color 0.15s;
        }

        .suv__fine-print a:hover {
          color: var(--fg-muted);
        }

        .suv__signout {
          margin-top: 6px;
          font-family: var(--font-inter, 'Inter', system-ui, sans-serif);
          font-size: 12px;
          color: var(--fg-faint);
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.15s;
          opacity: 0;
          animation: suv-fade-up 0.5s ease forwards 0.8s;
        }

        .suv__signout:hover { color: var(--fg-subtle); }
      `}</style>

      <div className="suv">
        <div className="suv__content">
          {/* Connection visualization */}
          <div className="suv__viz">
            <div className="suv__line suv__line--left" />
            <div className="suv__dot suv__dot--ok" />

            <div className="suv__line suv__line--right" />
            <div className="suv__dot suv__dot--fail" />
            <div className="suv__x">
              <TinyXIcon />
            </div>

            {/* You node */}
            <div className="suv__node suv__node--you">
              <div className="suv__avatar">
                <UserSilhouetteFilledIcon />
              </div>
            </div>

            {/* Vestra logo node */}
            <div className="suv__node suv__node--logo">
              <img src="/vestra.svg" alt="Vestra" className="suv__logo-icon" />
            </div>

            {/* Server node */}
            <div className="suv__node suv__node--server">
              <div className="suv__server-bars">
                <div className="suv__server-bar" />
                <div className="suv__server-bar" />
                <div className="suv__server-bar" />
              </div>
            </div>

            <div className="suv__labels">
              <span className="suv__label">You</span>
              <span className="suv__label suv__label--center">Vestra</span>
              <span className="suv__label">Server</span>
            </div>
          </div>

          {/* Status pill */}
          <div className="suv__pill">
            <div className="suv__pill-dot" />
            <span className="suv__pill-text">Reconnecting</span>
          </div>

          <h1 className="suv__heading">
            {heading ?? "Server temporarily unavailable"}
          </h1>
          <p className="suv__subtext">
            {subtext ?? (
              <>
                Your agents and data are safe. The backend is momentarily
                unreachable &mdash; we&apos;ll reconnect automatically.
              </>
            )}
          </p>

          {/* Action card */}
          <div className="suv__card">
            <button
              className="suv__retry-btn"
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? (
                <>
                  <div className="suv__spinner" />
                  <span>Connecting...</span>
                </>
              ) : (
                <span>Retry now</span>
              )}
            </button>

            {autoRetry && (
              <div className="suv__footer">
                <span className="suv__countdown">
                  Auto-retry in <span>{seconds}</span>s
                </span>
                <svg className="suv__ring" viewBox="0 0 28 28">
                  <circle className="suv__ring-bg" cx="14" cy="14" r="12" />
                  <circle
                    className="suv__ring-fill"
                    cx="14"
                    cy="14"
                    r="12"
                    strokeDasharray={circumference}
                    strokeDashoffset={ringOffset}
                  />
                </svg>
              </div>
            )}
          </div>

          <button onClick={signOut} className="suv__signout">
            Sign out
          </button>
        </div>
      </div>
    </>
  );
}
